<script setup lang="ts">
import axios from 'axios'

const route = useRoute()
const router = useRouter()
const { locale, t: $t } = useI18n()
const { objectiveSlug } = useStore()

const LOGO = '/images/logo_hp_dark.svg'

const AUTOLOGIN_TIMEOUT_MS = 15000

const MIN_LOADER_DISPLAY_MS = 600

const skipQuestion = ref<boolean>()
const setupTokenFromRoute = ref<string>()
const hasFailed = ref(false)
let timeoutId: ReturnType<typeof setTimeout> | undefined
let mountedAt = 0

const { resetUseStorageItems, jwt, user, setupToken, isSupportConnection } = useLocalStorage()

const routeLocale = route.query?.lang

if (!routeLocale) {
  isSupportConnection.value = route.query.skipQuestion ? true : false
  setupTokenFromRoute.value = Array.isArray(route.query.onboardingToken)
    ? route.query.onboardingToken[0]
    : route.query.onboardingToken
  skipQuestion.value = route.query.skipQuestion ? true : false
}

const { login, loginWithLang, authUserWithRouteToken, logInSignUpUser } = useAuth()

const autoLogin = async () => {
  resetUseStorageItems()

  const authAutoLoginPayload = {
    token: route.query.token
  }

  const authAutoLogin = await axios.post('auth/autologin', authAutoLoginPayload)
  jwt.value = authAutoLogin.data.data.token

  const requestAuthMe = await axios.get('auth/me')
  user.value = requestAuthMe.data.data

  const merchantId = requestAuthMe.data.data.merchantId

  await login(merchantId)

  isSupportConnection.value = skipQuestion.value ? skipQuestion.value : false
  if (setupTokenFromRoute.value) {
    setupToken.value = setupTokenFromRoute.value
    await router.push({ name: 'Setup' })
  } else {
    setupToken.value = ''
    await router.push({ name: 'Home' })
  }
}

const page: ComputedRef<'LoginLang' | 'LoginAfterSignUp' | 'Login' | 'LoginSocial' | 'SignUpSocial'> = computed(() => {
  if (routeLocale) {
    return 'LoginLang'
  }
  if (route.query.type === 'login' && route.query.from === 'signup') {
    return 'LoginAfterSignUp'
  }
  if (route.query.type === 'signup' && !!route.query.social) {
    return 'SignUpSocial'
  }
  if (route.query.type === 'login' && !!route.query.social) {
    return 'LoginSocial'
  }
  return 'Login'
})

const text = computed(() => {
  switch (page.value) {
    case 'LoginLang':
      return 'login.loadingLang'
    case 'LoginAfterSignUp':
      return 'login.loadingSignUp'
    case 'SignUpSocial':
      return 'login.waiting'
    default:
      return 'login.loading'
  }
})

const subtitle = computed(() => {
  if (page.value === 'LoginAfterSignUp') {
    const loadingSignUpSubtitle = 'login.loadingSignUpSubtitle'
    switch (objectiveSlug.value) {
      default:
        return `${loadingSignUpSubtitle}.default`
      case 'google':
        return `${loadingSignUpSubtitle}.google`
      case 'loyalty-program':
        return `${loadingSignUpSubtitle}.loyalty`
    }
  } else {
    return null
  }
})

const backToLogin = () => {
  resetUseStorageItems()
  router.push({ name: 'Login' })
}

const fail = async () => {
  const remaining = MIN_LOADER_DISPLAY_MS - (Date.now() - mountedAt)
  if (remaining > 0) {
    await new Promise((resolve) => setTimeout(resolve, remaining))
  }
  hasFailed.value = true
}

onMounted(async () => {
  mountedAt = Date.now()
  timeoutId = setTimeout(fail, AUTOLOGIN_TIMEOUT_MS)

  try {
    if (page.value === 'LoginLang') {
      locale.value = routeLocale.toString()
      await loginWithLang()
      return
    }
    if (page.value === 'LoginAfterSignUp') {
      await logInSignUpUser()
      return
    }
    if (page.value === 'LoginSocial' || page.value === 'SignUpSocial') {
      await authUserWithRouteToken()
      return
    }
    await autoLogin()
  } catch {
    await fail()
  } finally {
    clearTimeout(timeoutId)
  }
})

onUnmounted(() => clearTimeout(timeoutId))
</script>

<template>
  <div class="bg-auth-background bg-auth-grain relative z-0 flex h-screen items-center justify-center">
    <div class="animate-fade-in flex w-full max-w-md flex-col items-center px-6">
      <img :src="LOGO" class="h-10 w-auto" :class="{ 'animate-beat': !hasFailed }" alt="Pongo" />

      <template v-if="hasFailed">
        <p class="mt-10 text-center font-medium text-foreground lg:text-lg">
          {{ $t('login.autologinError.title') }}
        </p>
        <p class="mt-1 text-center text-sm text-muted-foreground">
          {{ $t('login.autologinError.subtitle') }}
        </p>
        <Button variant="default-primary" class="mt-6" @click="backToLogin">
          {{ $t('login.autologinError.action') }}
        </Button>
      </template>

      <template v-else>
        <Progress :model-value="null" class="mt-10 h-1 w-40" />
        <p class="mt-6 text-center font-medium text-foreground lg:text-lg">{{ $t(text) }}</p>
        <p v-if="subtitle" class="mt-1 text-center font-medium text-muted-foreground lg:text-lg">
          {{ $t(subtitle) }}
        </p>
      </template>
    </div>
  </div>
</template>
